import { useEffect, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { HelmetProvider } from "react-helmet-async";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import Services from "@/pages/Services";
import Products from "@/pages/Products";
import ProductDetail from "@/pages/ProductDetail";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import CanvaPage from "@/pages/CanvaPage";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import EventRegistrationBelgium from "@/pages/landing/EventRegistrationBelgium";
import OnsiteBadgingBelgium from "@/pages/landing/OnsiteBadgingBelgium";
import BrandActivationBelgium from "@/pages/landing/BrandActivationBelgium";
import PhotoboothRentalBelgium from "@/pages/landing/PhotoboothRentalBelgium";
import EventAccreditationSystems from "@/pages/landing/EventAccreditationSystems";
import NotFound from "@/pages/NotFound";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { SettingsProvider } from "@/contexts/SettingsContext";

// Code-split admin so it never ships in the public bundle
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/pages/admin/AdminLayout"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminSettings = lazy(() => import("@/pages/admin/AdminSettings"));
const AdminProducts = lazy(() => import("@/pages/admin/AdminProducts"));
const AdminInquiries = lazy(() => import("@/pages/admin/AdminInquiries"));
const AdminPages = lazy(() => import("@/pages/admin/AdminPages"));

function AdminBoundary({ children }) {
  return (
    <Suspense
      fallback={
        <div className="min-h-screen flex items-center justify-center bg-ot-bg">
          <div className="font-display text-xl uppercase tracking-widest">Loading…</div>
        </div>
      }
    >
      {children}
    </Suspense>
  );
}

function ProtectedRoute({ children }) {
  const { user, checking } = useAuth();
  if (checking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-ot-bg">
        <div className="font-display text-xl uppercase tracking-widest">Loading…</div>
      </div>
    );
  }
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function AppShell() {
  useEffect(() => {
    // Disable Lenis on touch devices and when the user prefers reduced motion
    const prefersReduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const isTouch = window.matchMedia("(pointer: coarse)").matches;
    if (prefersReduced || isTouch) return;

    let lenis;
    let raf;
    (async () => {
      const Lenis = (await import("lenis")).default;
      lenis = new Lenis({
        duration: 1.1,
        easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
        smoothWheel: true,
      });
      const tick = (time) => {
        lenis.raf(time);
        raf = requestAnimationFrame(tick);
      };
      raf = requestAnimationFrame(tick);
    })();

    return () => {
      if (raf) cancelAnimationFrame(raf);
      if (lenis) lenis.destroy();
    };
  }, []);

  return (
    <Routes>
      {/* Admin */}
      <Route path="/admin/login" element={<AdminBoundary><AdminLogin /></AdminBoundary>} />
      <Route
        path="/admin"
        element={
          <ProtectedRoute>
            <AdminBoundary><AdminLayout /></AdminBoundary>
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminBoundary><AdminDashboard /></AdminBoundary>} />
        <Route path="settings" element={<AdminBoundary><AdminSettings /></AdminBoundary>} />
        <Route path="products" element={<AdminBoundary><AdminProducts /></AdminBoundary>} />
        <Route path="pages" element={<AdminBoundary><AdminPages /></AdminBoundary>} />
        <Route path="inquiries" element={<AdminBoundary><AdminInquiries /></AdminBoundary>} />
      </Route>

      {/* Public */}
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/services" element={<Services />} />
        <Route path="/products" element={<Products />} />
        <Route path="/products/:slug" element={<ProductDetail />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/event-registration-belgium" element={<EventRegistrationBelgium />} />
        <Route path="/onsite-badging-belgium" element={<OnsiteBadgingBelgium />} />
        <Route path="/brand-activation-belgium" element={<BrandActivationBelgium />} />
        <Route path="/photobooth-rental-belgium" element={<PhotoboothRentalBelgium />} />
        <Route path="/event-accreditation-systems" element={<EventAccreditationSystems />} />
        <Route path="/p/:slug" element={<CanvaPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <AuthProvider>
          <SettingsProvider>
            <BrowserRouter>
              <AppShell />
            </BrowserRouter>
            <Toaster position="top-right" richColors />
          </SettingsProvider>
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
